import { createFileRoute } from "@tanstack/react-router";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import { Switch } from "../../../../components/fields/base/Switch";
import { useFetch } from "../../../../hooks/api";
import useDefaultState from "../../../../hooks/useDefaultState";
import firstPartyDataTemplateUrl from "/first_party_data_template.csv?url";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "../../../../redux/hooks";
import api from "../../../../utils/api";
import { css } from "../../../../../styled-system/css";
import { Container } from "react-bootstrap";
import { Tabs, TabList, Tab, TabPanel } from "../../../../components/core/basic/Tabs";
import { Button } from "../../../../components/core/basic/Button";
import { HStack, VStack } from "../../../../../styled-system/jsx";
import useQueryParams from "../../../../hooks/useQueryParams";
import Papa from "papaparse";
import SelectField from "../../../../components/core/basic/SelectField";
import { SearchInput, TextField } from "../../../../components/fields/base/TextField";
import { Modal } from "../../../../components/core/basic/Modal";
import { useSimpleCan } from "../../../../config/casl/casl";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../../../../components/core/blocks/MetricSelector";
import { createAppColumnHelper, useClientTable } from "../../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { commaSearchFilterFn } from "../../../../components/table/filterFns";
import { resetTable } from "../../../../components/table/utils";
import { RT8Pagination } from "../../../../components/table/Pagination";
import { Table } from "../../../../components/table/Table";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";

const CreateDataLinkCustomUploadModal = ({ show, setShow, amcInstances }) => {
  const { t } = useTranslation(["amc"]);
  const inputRef = useRef();
  const queryClient = useQueryClient();
  const [selectAMCInstance, setSelectAMCInstance] = useDefaultState(amcInstances?.[0]?.id);
  const [name, setName] = useState("");
  const [uplodatedData, setUploadedData] = useState(null);
  const [errors, setErrors] = useState([]);
  const userCanUploadCustomData = useSimpleCan("create", "AMCAudience");
  const createDataLink = useMutation({
    mutationFn: (body) => {
      return api.post("/amc/datalink/upload/", {
        ...body,
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datalinks"],
      });
      setShow(false);
    },
  });
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.addEventListener("load", (e) => {
        setErrors([]);
        const text = e.target.result;
        const { data, errors } = Papa.parse(text, {
          header: true,
          quoteChar: '"',
          escapeChar: "\\",
        });
        setErrors(errors);
        if (errors.length > 0) {
          inputRef.current.value = "";
          return;
        }
        setUploadedData(data);
      });
      reader.readAsText(file);
    }
  };
  return (
    <Modal
      isOpen={show}
      onOpenChange={(isOpen) => setShow(isOpen)}
      title={t(($) => $.amc["Upload Custom Data"])}
      css={{
        width: "60vw",
        padding: "xlarge",
      }}
    >
      <VStack gap={16}>
        <p className="text-center">
          {t(
            ($) =>
              $.amc["Upload your custom data to import 1st Party Data into AMC. The file should be in CSV format."],
          )}
        </p>
        <VStack gap="4">
          <HStack gap="16">
            {amcInstances?.length > 1 && (
              <SelectField
                label={t(($) => $.amc["AMC Instance"])}
                value={selectAMCInstance}
                onChange={(e) => {
                  setSelectAMCInstance(e.target.value);
                }}
                options={amcInstances.map((instance) => {
                  return {
                    label: instance?.raw?.instanceName,
                    value: instance.id,
                  };
                })}
              />
            )}
            <TextField
              css={{
                flexDirection: "row",
                alignItems: "center",
              }}
              label={t(($) => $.amc["Name"])}
              value={name}
              onChange={(val) => setName(val)}
            />
            <input type="file" ref={inputRef} accept={"text/csv"} onChange={handleUpload} />
          </HStack>
          {errors.length > 0 && (
            <div className={css({ color: "error" })}>
              {t(($) => $.amc["Errors found in the uploaded file:"])}
              <ul
                className={css({
                  paddingLeft: "medium",
                  marginTop: "xxsmall",
                  display: "flex",
                  flexDirection: "column",
                  gap: "xxsmall",
                })}
              >
                {errors.map((error, index) => {
                  const meta = [];
                  if (typeof error.row === "number") {
                    meta.push(`row ${error.row + 1}`);
                  }
                  return (
                    <li key={`${error.code || "error"}-${error.row ?? "unknown"}-${index}`}>
                      {error.message}
                      <span>{meta.length > 0 ? ` (${meta.join(", ")})` : ""}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </VStack>
        <HStack justifyContent="space-between" width="full">
          <a target="_blank" rel="noreferrer" href={firstPartyDataTemplateUrl}>
            {t(($) => $.amc["Download Template"])}
          </a>
          <HStack justifyContent="end" gap="2">
            <Button
              variant="secondary"
              onPress={() => {
                inputRef.current.value = "";
                setErrors([]);
                setUploadedData(null);
                setShow(false);
              }}
            >
              {t(($) => $.amc["Cancel"])}
            </Button>
            <Button
              onPress={() => {
                createDataLink.mutate({
                  marketplace: "US",
                  data: uplodatedData,
                  amc_instance: selectAMCInstance,
                  name,
                });
              }}
              isPending={createDataLink.isPending}
              isDisabled={!uplodatedData || errors?.length > 0 || !userCanUploadCustomData}
            >
              {t(($) => $.amc["Continue"])}
            </Button>
          </HStack>
        </HStack>
      </VStack>
    </Modal>
  );
};

const columnHelper = createAppColumnHelper();

const uploadSearchFilterFn = commaSearchFilterFn((row) => `${row.name}`);

const DataLinkCustomUploadTable = ({ dataLinks, isLoading, amcInstances }) => {
  const [show, setShow] = useState(false);
  const userCanUploadCustomData = useSimpleCan("create", "AMCAudience");
  const { t } = useTranslation(["amc", "common"]);
  const dateFormatter = useDateFormatter("medium");
  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "amc-custom-upload-table",
    initialState: { sorting: [{ id: "created_on", desc: true }] },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(($) => $.amc["Upload"]),
        size: 250,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((row) => row.amc_instance?.raw?.instanceName ?? undefined, {
        id: "amcInstanceName",
        header: t(($) => $.amc["AMC Instance"]),
        size: 200,
        sortFn: "alphanumeric",
        sortUndefined: "last",
      }),
      columnHelper.accessor((row) => row.upload_status?.[0]?.upload_status ?? undefined, {
        id: "uploadStatus",
        header: t(($) => $.amc["Status"]),
        size: 140,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
      }),
      columnHelper.accessor("created_on", {
        header: t(($) => $.amc["Created On"]),
        size: 140,
        enableResizing: false,
        cell: ({ getValue }) => (getValue() ? dateFormatter.format(new Date(getValue())) : "-"),
      }),
    ],
    [t, dateFormatter],
  );

  const table = useClientTable({
    data: dataLinks,
    columns,
    getRowId: (row) => String(row.id),
    globalFilterFn: uploadSearchFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isLoading,
    },
  });

  return (
    <>
      <CreateDataLinkCustomUploadModal show={show} setShow={setShow} amcInstances={amcInstances} />
      {userCanUploadCustomData ?
        <HStack justifyContent="end">
          <Button
            onPress={() => {
              setShow(true);
            }}
          >
            {t(($) => $.amc["Upload"])}
          </Button>
        </HStack>
      : null}
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};

const DataLinkConfiguration = ({ dataLinkMap, instance, org }) => {
  const queryClient = useQueryClient();
  const userCanUpdateDataLinks = useSimpleCan("update", "AMCAudience");
  const { t } = useTranslation(["amc"]);
  const createDataLink = useMutation({
    mutationFn: (body) => {
      return api.post("/amc/datalink/", {
        ...body,
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datalinks"],
      });
    },
  });
  const updateDataLink = useMutation({
    mutationFn: (dataLink) => {
      return api.patch(`/amc/datalink/${dataLink.id}/`, dataLink);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datalinks"],
      });
    },
  });
  const dataLink = dataLinkMap[`${org.id}-${instance.id}`];
  const [enabled, setEnabled] = useDefaultState(dataLink?.enabled || false);

  const handleSwitchChange = (orgId, amcInstanceId, enabled) => {
    setEnabled(enabled);
    const dataLink = dataLinkMap[`${orgId}-${amcInstanceId}`];
    if (dataLink) {
      updateDataLink.mutate({
        id: dataLink.id,
        enabled,
      });
    } else {
      createDataLink.mutate({ shopify_org: orgId, amc_instance: amcInstanceId, enabled, marketplace: "US" });
    }
  };
  const status = dataLink?.upload_status?.[0]?.upload_status;
  return (
    <div>
      <p className={css({ marginBottom: "2rem" })}>
        {t(
          ($) =>
            $.amc[
              "Sync your Shopify customer data into AMC so you can create custom audiences based on your shopify customers."
            ],
        )}
      </p>
      <Switch
        isSelected={enabled}
        onChange={(e) => {
          handleSwitchChange(org.id, instance.id, e);
        }}
        isDisabled={!userCanUpdateDataLinks}
      >
        {t(($) => $.amc["Enabled AMC 1st Party Data"])}
        {enabled && status && status !== "SUCCESS" && (
          <span
            title={status}
            className={css({
              marginLeft: "10px",
              backgroundColor: status === "FAILED" ? "red" : "green",
            })}
          >
            {status}
          </span>
        )}
      </Switch>
    </div>
  );
};

const AMCConfiguration = () => {
  const [tab, setTab] = useQueryParams("tab", "shopify");
  const { t } = useTranslation(["amc"]);
  const { organization_id: orgId } = useAppSelector((state) => state.user);
  const { data: dataLinks, isLoading } = useFetch(
    ["datalinks", orgId],
    "/amc/datalink/",
    {},
    {
      select: (res) => res.data.results,
    },
  );
  const { data: orgs } = useFetch(
    ["orgs", orgId],
    "/api/organizations",
    {
      org_type: "shopify",
    },
    {
      select: (res) => res.data,
    },
  );
  const { data: amcInstances } = useFetch(
    ["amcInstances", orgId],
    "/amc/instances/",
    {},
    {
      select: (res) => res.data,
    },
  );

  const dataLinkMap = useMemo(() => {
    return dataLinks?.reduce((acc, dataLink) => {
      const key = `${dataLink.shopify_org?.id}-${dataLink.amc_instance?.id}`;
      acc[key] = dataLink;
      return acc;
    }, {});
  }, [dataLinks]);

  const customUploads = useMemo(() => {
    return dataLinks?.filter((dataLink) => dataLink.data_source === "USER_UPLOAD") || [];
  }, [dataLinks]);

  return (
    <PageContainer>
      <Container>
        <h1
          className={css({
            fontSize: "24px",
            fontWeight: "600",
            textAlign: "center",
            marginBottom: "20px",
          })}
        >
          {t(($) => $.amc["AMC Configuration"])}
        </h1>
        <h3
          className={`fs-2 ${css({
            fontWeight: "600",
            marginBottom: "20px",
          })}`}
        >
          {t(($) => $.amc["First Party Data Connections"])}
        </h3>
        <Tabs selectedKey={tab} onSelectionChange={setTab}>
          <TabList>
            <Tab id="shopify">{t(($) => $.amc["Shopify"])}</Tab>
            <Tab id="custom">{t(($) => $.amc["Custom Upload"])}</Tab>
          </TabList>
          <TabPanel id="shopify">
            {orgs?.length === 0 && (
              <div
                className={css({
                  padding: "15px",
                  lineHeight: "150px",
                  fontWeight: "bold",
                  fontSize: "1.5rem",
                  color: "gray.500",
                  background: "gray.100",
                  borderRadius: "15px",
                  textAlign: "center",
                  height: "150px",
                })}
              >
                <span>{t(($) => $.amc["No Shopify organizations found."])}</span>
              </div>
            )}
            {orgs?.map((org) => {
              return (
                <div
                  className={`card fs-stardard ${css({ paddingTop: "15px", paddingBottom: "15px", paddingLeft: "20px", paddingRight: "20px" })}`}
                  key={org.id}
                >
                  <h5 className={css({ fontWeight: "bold" })}>{org.profile_name}</h5>
                  {amcInstances &&
                    amcInstances.map((instance) => {
                      return (
                        <DataLinkConfiguration
                          key={instance.id}
                          dataLinkMap={dataLinkMap}
                          instance={instance}
                          org={org}
                        />
                      );
                    })}
                </div>
              );
            })}
          </TabPanel>
          <TabPanel id="custom">
            <div
              className={`card fs-stardard ${css({ paddingTop: "15px", paddingBottom: "15px", paddingLeft: "20px", paddingRight: "20px" })}`}
            >
              <DataLinkCustomUploadTable amcInstances={amcInstances} dataLinks={customUploads} isLoading={isLoading} />
            </div>
          </TabPanel>
        </Tabs>
      </Container>
    </PageContainer>
  );
};

export const Route = createFileRoute("/_authenticated/user/amc/configuration")({
  component: AMCConfiguration,
  staticData: { application: "Advertising" },
});
